import React, { memo } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

/**
 * Modern Pagination component with clean design and improved UX.
 * Memoized for optimal performance.
 */
export const Pagination = memo(({ totalPage, currentPage, getData, reduxCall = true }) => {
    const { perPage } = useSelector((state) => state.context)
    const dispatch = useDispatch()

    const handlePageChange = (page) => {
        if (reduxCall) {
            dispatch(getData({ perPage, currentPage: page }))
        } else {
            getData({ perPage, currentPage: page })
        }
    }

    const renderPageButton = (page, isActive = false) => (
        <button
            key={page}
            onClick={() => handlePageChange(page)}
            className={`
                min-w-[36px] h-9 px-3 rounded-lg text-sm font-medium
                transition-all duration-150
                ${isActive 
                    ? 'bg-primary-500 text-white shadow-sm' 
                    : 'bg-white text-text-secondary hover:bg-background-hover hover:text-text-primary border border-border'
                }
            `}
        >
            {page}
        </button>
    )

    const renderEllipsis = (key) => (
        <span key={key} className="min-w-[36px] h-9 px-3 flex items-center justify-center text-text-tertiary">
            ...
        </span>
    )

    const renderPageNumbers = () => {
        const pages = []
        
        if (currentPage > 2 && totalPage > 3) {
            pages.push(renderPageButton(1))
        }
        
        if (currentPage > 3 && totalPage > 3) {
            pages.push(renderEllipsis('ellipsis-start'))
        }
        
        if (currentPage !== 1 && currentPage - 2 !== 0 && totalPage > currentPage - 1) {
            pages.push(renderPageButton(currentPage - 2))
        }
        
        if (currentPage !== 1) {
            pages.push(renderPageButton(currentPage - 1))
        }
        
        pages.push(renderPageButton(currentPage, true))
        
        if (currentPage !== totalPage) {
            pages.push(renderPageButton(currentPage + 1))
        }
        
        if (currentPage === 1 && totalPage > currentPage + 1) {
            pages.push(renderPageButton(currentPage + 2))
        }
        
        if (totalPage > currentPage + 2 && totalPage !== 3) {
            pages.push(renderEllipsis('ellipsis-end'))
        }
        
        if (currentPage < totalPage - 1 && totalPage > currentPage + 1 && totalPage !== 3) {
            pages.push(renderPageButton(totalPage))
        }
        
        return pages
    }

    if (totalPage <= 1) return null

    return (
        <div className="flex items-center justify-between py-4">
            <div className="hidden sm:flex items-center gap-2 text-sm text-text-secondary">
                Page <span className="font-medium text-text-primary">{currentPage}</span> of{' '}
                <span className="font-medium text-text-primary">{totalPage}</span>
            </div>

            <div className="flex items-center gap-2">
                <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`
                        h-9 px-3 rounded-lg text-sm font-medium
                        flex items-center gap-1.5
                        transition-all duration-150
                        ${currentPage === 1
                            ? 'text-text-disabled cursor-not-allowed'
                            : 'text-text-secondary hover:text-text-primary hover:bg-background-hover border border-border bg-white'
                        }
                    `}
                >
                    <ChevronLeftIcon className="h-4 w-4" />
                    <span className="hidden md:inline">Previous</span>
                </button>

                <div className="flex items-center gap-1">
                    {renderPageNumbers()}
                </div>

                <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPage}
                    className={`
                        h-9 px-3 rounded-lg text-sm font-medium
                        flex items-center gap-1.5
                        transition-all duration-150
                        ${currentPage === totalPage
                            ? 'text-text-disabled cursor-not-allowed'
                            : 'text-text-secondary hover:text-text-primary hover:bg-background-hover border border-border bg-white'
                        }
                    `}
                >
                    <span className="hidden md:inline">Next</span>
                    <ChevronRightIcon className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
});

Pagination.displayName = 'Pagination';

export default Pagination;
